(function () {
  'use strict';

  // ── The photo door (spec 7, 8g) ──
  //
  // Differences from the personalise door, all following from this being its own product:
  //   - The panel is open on arrival; there is no toggle.
  //   - No variant swap. This product's variants ALREADY carry the personalised price, so whatever
  //     the customer picked is the right variant (spec 8g).
  //   - The artwork is the customer's file, so there are two server calls: the photo is uploaded
  //     first for an uploadId, then the design state is rendered against it.
  //
  // The preview needs no server at all: FileReader plus canvas draws the chosen photo onto the case
  // already on the page, so the customer sees it immediately rather than after a round trip.

  var root = document.querySelector('[data-photo-case]');
  if (!root) return;

  var fileInput = root.querySelector('[data-photo-input]');
  var dropZone = root.querySelector('[data-photo-drop]');
  var dropTitle = root.querySelector('[data-photo-drop-title]');
  var chosenBox = root.querySelector('[data-photo-chosen]');
  var chosenThumb = root.querySelector('[data-photo-thumb]');
  var chosenName = root.querySelector('[data-photo-name]');
  var changeBtn = root.querySelector('[data-photo-change]');
  var errorEl = root.querySelector('[data-photo-error]');
  var canvas = root.querySelector('[data-photo-canvas]');
  var acks = Array.prototype.slice.call(root.querySelectorAll('[data-photo-ack]'));
  if (!fileInput || !canvas) return;

  var deviceIndex = parseInt(root.dataset.deviceIndex, 10);
  var caseTypeIndex = parseInt(root.dataset.caseTypeIndex, 10);
  var currentOptions = [];
  currentOptions[deviceIndex >= 0 ? deviceIndex : 0] = root.dataset.initialDevice || '';
  if (caseTypeIndex >= 0) currentOptions[caseTypeIndex] = root.dataset.initialCaseType || '';

  var slug = function (s) {
    return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  };

  // Mirrors lib/builder/uploads.js on the server. Checked here too so an obviously wrong file is
  // refused instantly rather than after uploading 25MB; the server's check is the one that counts,
  // because anything here can be bypassed.
  var MAX_BYTES = 25 * 1024 * 1024;
  var SOFT_MIN_SHORTEST_SIDE = 1000;
  var HARD_MIN_SHORTEST_SIDE = 600;

  var chosenFile = null;      // the File the customer picked
  var chosenDataUrl = null;   // its data URL, for the local preview
  var uploadId = null;        // set once the server has it; cleared whenever the file changes
  var softWarning = null;

  function showError(message) {
    if (!errorEl) return;
    errorEl.textContent = message || '';
    errorEl.hidden = !message;
  }

  // ── Drawing the customer's photo onto the case already on screen ──────────────────────────
  //
  // The same approach as the personalise door: rather than rebuilding the case in Canvas 2D, the
  // photo is drawn over the product gallery's own render, clipped to the case's face. One renderer
  // stays authoritative, and the preview cannot drift from it in ways a second implementation would.
  // The case outline, per model, from the renderer's own caseBox (assets/case-camera-rects.json).
  //
  // These were hardcoded at first — one aspect ratio and one corner radius for all 32 models — and
  // both were wrong: 0.496 against a real 0.489, and a 0.09 corner radius against a real 0.160. The
  // photo therefore overhung the case outline at the corners and edges, and the plate's white
  // ground blended over the overhang as a bright fringe all the way round.
  var FALLBACK_BOX = { w: 0.455, h: 0.9305, r: 0.16044 };   // iPhone 16 Pro, the default model

  function currentBox() {
    var m = cameraRects && cameraRects[slug(currentOptions[deviceIndex >= 0 ? deviceIndex : 0])];
    return (m && m.box) || FALLBACK_BOX;
  }

  function galleryImage() {
    var gallery = document.querySelector('[data-product-images]');
    if (!gallery) return null;
    return gallery.querySelector('.product-main-slide.is-selected img')
        || gallery.querySelector('.product-main-slide.starting-slide img')
        || gallery.querySelector('.product-main-slide img');
  }

  var photoImg = null;

  // ── The camera opening, per model ─────────────────────────────────────────────────────────
  //
  // Spec 8c: the customer must SEE their photo against the real camera opening for the phone they
  // picked. Coconut Lane describe the crop in prose because they cannot draw it; we hold the
  // measured geometry, so drawing it is the better warning.
  //
  // Without this the photo covered the whole face including the camera module, which hid the one
  // thing the warning is about. The rects are generated from the renderer's own caseBox/cutoutBox
  // (assets/case-camera-rects.json), as fractions of the case box, so the preview cannot disagree
  // with the print. The variation is real: the 17 Pro Max opening is 84% of the case width against
  // 51% on the 16 Pro.
  var cameraRects = null;
  var cameraRectsPromise = null;

  function loadCameraRects() {
    if (cameraRectsPromise) return cameraRectsPromise;
    var url = root.dataset.cameraRectsUrl;
    if (!url || typeof fetch !== 'function') return Promise.resolve(null);
    cameraRectsPromise = fetch(url)
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) { cameraRects = j; draw(); return j; })
      .catch(function () { return null; });
    return cameraRectsPromise;
  }

  function currentCameraRect() {
    if (!cameraRects) return null;
    var m = cameraRects[slug(currentOptions[deviceIndex >= 0 ? deviceIndex : 0])];
    return (m && m.cam) || null;
  }

  function roundedRect(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  }

  // ── The shading plate ─────────────────────────────────────────────────────────────────────
  //
  // Painting the photo straight onto the case destroyed everything the renderer builds ON TOP of a
  // design: gloss, bevel, inner shadow, button notches, drop shadow. The preview came out as a flat
  // rectangle of photo, which is not the product.
  //
  // A plate is the case rendered with a MID-GREY design and no logo (see
  // scripts/case-templates/build-shade-plates.js), so it carries the lighting and nothing else.
  // Drawing it over the photo with an `overlay` blend puts the photo UNDER the shading, which is
  // the order the real renderer composites in: mid-grey is the neutral point of that blend, so the
  // face passes through untouched and only the renderer's departures from grey modulate the photo.
  var plateCache = {};
  var sampleImg = null;

  function sampleImage() {
    var url = root.dataset.sampleUrl;
    if (!url) return null;
    if (sampleImg) return sampleImg;
    sampleImg = new Image();
    sampleImg.crossOrigin = 'anonymous';
    sampleImg.onload = draw;
    sampleImg.onerror = function () { sampleImg = null; };
    sampleImg.src = url;
    return sampleImg;
  }

  function plateUrl(modelId) {
    var tpl = root.dataset.shadePlateUrl;
    if (!tpl) return null;
    return tpl.replace('iphone-16-pro', modelId);
  }

  function plateFor(modelId) {
    if (!modelId) return null;
    if (plateCache[modelId] !== undefined) return plateCache[modelId];
    var url = plateUrl(modelId);
    if (!url) return (plateCache[modelId] = null);
    var img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = draw;
    // A model with no plate simply gets the unshaded preview rather than a broken one.
    img.onerror = function () { plateCache[modelId] = null; };
    img.src = url;
    plateCache[modelId] = img;
    return img;
  }

  function draw() {
    var img = galleryImage();
    if (!img) return;
    // Attached unconditionally and WITHOUT `once`. The theme swaps this element's `src` on every
    // variant change, so a one-shot listener fires for the first image and never again: every later
    // swap left the canvas sized and positioned for the PREVIOUS image. On the photo case, where the
    // variant image really does change, that showed as the "Personalised" marker flashing up with
    // the case drawn offset over it.
    //
    // Safe to call on every draw: addEventListener de-duplicates an identical type + function +
    // capture triple, so this never stacks listeners.
    img.addEventListener('load', draw);
    if (!img.complete || !img.naturalWidth) return;
    var rect = img.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    canvas.width = Math.round(rect.width);
    canvas.height = Math.round(rect.height);
    canvas.style.width = rect.width + 'px';
    canvas.style.height = rect.height + 'px';
    canvas.style.top = rect.top + 'px';
    canvas.style.left = rect.left + 'px';

    var ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Nothing chosen yet: draw the SAMPLE photo through the same pipeline, so the page always shows
    // a real case. This is not decoration. The checkout line has to show the "Personalised" marker
    // rather than a case nobody designed (Alexis, 2026-09-12), and checkout reads the VARIANT image
    // — which is also what this theme's gallery shows. Drawing the case here means the marker can
    // own the variant image for checkout without the product page becoming a branded square.
    var subject = (photoImg && photoImg.naturalWidth) ? photoImg : sampleImage();
    if (!subject || !subject.naturalWidth) return;

    var box = currentBox();
    var faceW = box.w * canvas.width;
    var faceH = box.h * canvas.height;
    var faceX = (canvas.width - faceW) / 2;
    var faceY = (canvas.height - faceH) / 2;

    ctx.save();
    roundedRect(ctx, faceX, faceY, faceW, faceH, faceW * box.r);
    ctx.clip();

    // fit: cover, matching what the server does with the same photo, so the preview crops exactly
    // as the print will. A preview framed differently from the print is worse than none.
    var scale = Math.max(faceW / subject.naturalWidth, faceH / subject.naturalHeight);
    var drawW = subject.naturalWidth * scale;
    var drawH = subject.naturalHeight * scale;
    ctx.drawImage(subject, faceX + (faceW - drawW) / 2, faceY + (faceH - drawH) / 2, drawW, drawH);

    // The camera opening, cut out of the PHOTO so the plate's own camera module shows there at full
    // colour instead of being blended with whatever the photo happens to have behind it.
    var cam = currentCameraRect();
    if (cam) {
      ctx.globalCompositeOperation = 'destination-out';
      roundedRect(ctx, faceX + cam.x * faceW, faceY + cam.y * faceH,
                       cam.w * faceW, cam.h * faceH, cam.r * faceW);
      ctx.fill();
    }
    ctx.restore();


    var plate = plateFor(slug(currentOptions[deviceIndex >= 0 ? deviceIndex : 0]));
    if (plate && plate.complete && plate.naturalWidth) {
      // Over the WHOLE canvas, not just the face: outside the photo the destination is transparent,
      // so the blend leaves the plate itself — which is how the camera module, the button notches,
      // the drop shadow and the white ground arrive.
      ctx.globalCompositeOperation = 'overlay';
      ctx.drawImage(plate, 0, 0, canvas.width, canvas.height);
      ctx.globalCompositeOperation = 'source-over';
    }
  }

  // ── Choosing a file ───────────────────────────────────────────────────────────────────────
  function acceptFile(file) {
    if (!file) return;
    uploadId = null;      // a new file invalidates whatever the server holds for the old one
    softWarning = null;
    showError('');

    if (file.size > MAX_BYTES) {
      return showError('That photo is ' + (file.size / 1048576).toFixed(1) + 'MB. The limit is 25MB.');
    }

    var reader = new FileReader();
    reader.onerror = function () { showError('That photo could not be read, please try another.'); };
    reader.onload = function (e) {
      var probe = new Image();
      probe.onerror = function () {
        // HEIC is the expected case: browsers cannot decode it, but the server converts it, so the
        // file is still perfectly usable. What is lost is only the preview.
        chosenFile = file;
        chosenDataUrl = null;
        photoImg = null;
        showChosen(file, null);
        showError('We cannot preview this file type in the browser, but we can print it. Your case will be made from this photo.');
        gate();
      };
      probe.onload = function () {
        var shortest = Math.min(probe.naturalWidth, probe.naturalHeight);
        if (shortest < HARD_MIN_SHORTEST_SIDE) {
          chosenFile = null; photoImg = null; draw(); showChosen(null);
          gate();
          // Naming the actual numbers matters: "too small" alone leaves the customer guessing
          // whether a different photo will do any better.
          return showError('This photo is ' + probe.naturalWidth + ' x ' + probe.naturalHeight +
            ' pixels, which is too small to print well. We need at least 600 pixels on the shortest ' +
            'side, and 1000 or more looks best. Try the original from your camera roll rather than a ' +
            'copy from WhatsApp, Instagram or a web page.');
        }
        if (shortest < SOFT_MIN_SHORTEST_SIDE) {
          softWarning = 'This photo is on the small side and may print soft. Use the original from your camera roll rather than a copy from WhatsApp or Instagram.';
          showError(softWarning);
        }
        chosenFile = file;
        chosenDataUrl = e.target.result;
        photoImg = probe;
        showChosen(file, e.target.result);
        draw();
        gate();
      };
      probe.src = e.target.result;
    };
    reader.readAsDataURL(file);
  }

  function showChosen(file, dataUrl) {
    if (!chosenBox) return;
    if (!file) { chosenBox.hidden = true; if (dropTitle) dropTitle.textContent = 'Choose a photo'; return; }
    chosenBox.hidden = false;
    if (chosenName) chosenName.textContent = file.name;
    if (chosenThumb) {
      if (dataUrl) { chosenThumb.src = dataUrl; chosenThumb.style.display = ''; }
      else chosenThumb.style.display = 'none';
    }
    if (dropTitle) dropTitle.textContent = 'Photo chosen';
  }

  fileInput.addEventListener('change', function () { acceptFile(fileInput.files && fileInput.files[0]); });

  if (changeBtn) {
    changeBtn.addEventListener('click', function (e) {
      e.preventDefault();
      fileInput.value = '';
      chosenFile = null; chosenDataUrl = null; photoImg = null; uploadId = null;
      showChosen(null); showError(''); draw(); gate();
    });
  }

  // Drag and drop onto the same control. The label already opens the file picker on click, so this
  // only adds the drag path.
  if (dropZone) {
    ['dragenter', 'dragover'].forEach(function (type) {
      dropZone.addEventListener(type, function (e) { e.preventDefault(); dropZone.classList.add('is-dragover'); });
    });
    ['dragleave', 'drop'].forEach(function (type) {
      dropZone.addEventListener(type, function (e) { e.preventDefault(); dropZone.classList.remove('is-dragover'); });
    });
    dropZone.addEventListener('drop', function (e) {
      if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length) acceptFile(e.dataTransfer.files[0]);
    });
  }

  // ── Gating add-to-bag ─────────────────────────────────────────────────────────────────────
  //
  // Spec 8c: both acknowledgements, unticked by default, gate the purchase. A personalised case
  // cannot be resold, so a return is a total loss and the whole defence is pre-purchase.
  var addButton = document.querySelector('[data-product-blocks] [data-add-to-cart]');

  function acknowledged() {
    return acks.filter(function (a) { return a.checked; }).map(function (a) { return a.value; });
  }
  function ready() {
    return !!chosenFile && acknowledged().length === acks.length;
  }
  function gate() {
    if (addButton) addButton.disabled = !ready();
  }
  acks.forEach(function (a) { a.addEventListener('change', gate); });

  // ── Add to bag ────────────────────────────────────────────────────────────────────────────
  var SAVE_FAILED_MESSAGE = "We couldn't save your design, please try again";
  var UPLOAD_TIMEOUT_MS = 60000;   // a 25MB photo on a phone connection
  var RENDER_TIMEOUT_MS = 20000;   // measured server renders are 7-14s

  function builderUrl(path) {
    var base = (root.dataset.builderUrl || '').replace(/\/+$/, '');
    return base ? base + path : null;
  }

  function withTimeout(url, options, ms) {
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = setTimeout(function () { if (controller) controller.abort(); }, ms);
    if (controller) options.signal = controller.signal;
    return fetch(url, options).then(function (res) { clearTimeout(timer); return res; },
                                    function (err) { clearTimeout(timer); throw err; });
  }

  function readErrors(res) {
    return res.json().catch(function () { return {}; }).then(function (body) {
      var detail = body && body.errors && body.errors.length ? body.errors[0] : null;
      if (!res.ok) throw new Error(res.status < 500 && detail ? detail : SAVE_FAILED_MESSAGE);
      return body;
    });
  }

  // Uploaded once and remembered: changing nothing but the device should not re-send the photo.
  function ensureUploaded() {
    if (uploadId) return Promise.resolve(uploadId);
    var url = builderUrl('/uploads');
    if (!url) return Promise.reject(new Error(SAVE_FAILED_MESSAGE));
    var form = new FormData();
    form.append('photo', chosenFile, chosenFile.name);
    return withTimeout(url, { method: 'POST', body: form }, UPLOAD_TIMEOUT_MS)
      .then(readErrors)
      .then(function (body) {
        if (!body.uploadId) throw new Error(SAVE_FAILED_MESSAGE);
        uploadId = body.uploadId;
        // The server decodes the real file, so its verdict on size beats the browser's probe —
        // and for HEIC the browser had no verdict at all.
        if (body.warning) showError(body.warning);
        return uploadId;
      });
  }

  function designState(id) {
    return {
      version: 1,
      templateId: slug(currentOptions[deviceIndex >= 0 ? deviceIndex : 0]),
      caseType: /tough/i.test(String(caseTypeIndex >= 0 ? currentOptions[caseTypeIndex] : '')) ? 'tough' : 'hard',
      mode: 'photo',
      base: { kind: 'upload', uploadId: id },
      // No text in v1: photo-mode monogram legibility has no measured ladder to apply (there is no
      // design metafield to read one from), and the service returns 501 rather than invent one.
      text: null,
      acknowledged: acknowledged()
    };
  }

  var labelEl = document.querySelector('[data-product-blocks] [data-add-to-cart-text]');
  var savedLabel = null;
  function setBusy(on, message) {
    if (!labelEl) return;
    if (on) {
      if (savedLabel === null) savedLabel = labelEl.innerHTML;
      labelEl.textContent = message || 'Working...';
    } else if (savedLabel !== null) {
      labelEl.innerHTML = savedLabel;
      savedLabel = null;
    }
  }

  function addLine(saved) {
    var form = document.querySelector('form[action*="/cart/add"]');
    var quantityField = form && form.querySelector('[name="quantity"]');
    var quantity = quantityField ? parseInt(quantityField.value, 10) : 1;
    var idField = form && form.querySelector('[name="id"]');
    if (!idField) throw new Error(SAVE_FAILED_MESSAGE);

    var payload = {
      // No swap: this product's own variant already carries the personalised price (spec 8g).
      id: Number(idField.value),
      quantity: quantity > 0 ? quantity : 1,
      properties: {
        Personalisation: 'Your photo',
        Design: root.dataset.productTitle || 'Personalised Photo Case',
        _design_id: saved.designId,
        _preview: saved.thumbUrl || saved.previewUrl || ''
      }
    };
    var cartAdd = (window.theme && theme.routes && theme.routes.cartAdd) ||
      ((window.Shopify && Shopify.routes && Shopify.routes.root) || '/') + 'cart/add.js';
    return fetch(cartAdd, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(payload)
    }).then(function (res) {
      return res.json().catch(function () { return {}; }).then(function (json) {
        if (!res.ok) throw new Error(json && json.description ? json.description : SAVE_FAILED_MESSAGE);
        return json;
      });
    });
  }

  function afterAdded(line) {
    var form = document.querySelector('form[action*="/cart/add"]');
    if (form) {
      form.dispatchEvent(new CustomEvent('ajaxProduct:added', {
        detail: { product: line, addToCartBtn: addButton }, bubbles: true
      }));
    }
    if (window.theme && theme.settings && theme.settings.cartType === 'page') {
      window.location = theme.routes.cartPage;
    }
  }

  // Intercepting the CLICK in the capture phase, for the reason documented at length in
  // assets/pdp-personalise.js: theme.js's AjaxProduct binds to the form's submit and reads the form
  // synchronously, so no submit listener can get in front of it. The button is type="submit", so a
  // cancelled click fires no submit event at all.
  if (addButton) {
    document.addEventListener('click', function (e) {
      if (!e.target || !addButton.contains(e.target)) return;

      e.preventDefault();
      e.stopImmediatePropagation();

      if (!chosenFile) return showError('Choose a photo to put on your case.');
      if (acknowledged().length !== acks.length) {
        return showError('Please tick both boxes to confirm before adding to bag.');
      }

      showError(softWarning || '');
      setBusy(true, 'Uploading your photo...');

      ensureUploaded()
        .then(function (id) {
          setBusy(true, 'Making your case...');
          var url = builderUrl('/designs');
          return withTimeout(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(designState(id))
          }, RENDER_TIMEOUT_MS).then(readErrors);
        })
        .then(function (saved) {
          if (!saved.designId) throw new Error(SAVE_FAILED_MESSAGE);
          return addLine(saved);
        })
        .then(function (line) { setBusy(false); afterAdded(line); })
        .catch(function (err) {
          setBusy(false);
          showError((err && err.message) || SAVE_FAILED_MESSAGE);
        });
    }, true);
  }

  document.addEventListener('variant:change', function (e) {
    var v = e.detail && e.detail.variant;
    if (v && v.options) currentOptions = v.options;
    draw();
  });

  var galleryEl = document.querySelector('[data-product-images]');
  if (galleryEl && window.MutationObserver) {
    // 'src' and 'srcset' as well as 'class': Flickity moving `is-selected` is only one of the two
    // ways the visible image changes. The theme also swaps the src on the existing element for a
    // variant image, which changes nothing about the classes and so went unnoticed.
    new MutationObserver(draw).observe(galleryEl, {
      attributes: true,
      attributeFilter: ['class', 'src', 'srcset'],
      subtree: true
    });
  }
  window.addEventListener('resize', draw);
  window.addEventListener('scroll', draw, { passive: true });

  loadCameraRects();
  gate();
  draw();
})();
